// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";

import { mdiCog } from "@mdi/js";
import { Button, Popover, Select, Tooltip } from "antd";
import {
  BASE_WELL_LAYER,
  FACILITY_LABEL_LAYER,
  FACILITY_LAYER,
  FRAC_LAYER,
  GRID_LABEL_DEFAULT_SIZES,
  RIG_LAYER,
  SECTION_LABEL_LAYER,
  SHAPEFILE_DETAILS_LABEL_LAYER,
  SHAPEFILE_NAME_LABELS_LAYER,
  WELL_LABEL_LAYER,
  WELL_LAYER
} from "constants/mapLayers.constants";
import { RootState } from "store/rootReducer";
import {
  selectBaseLayerResourcePlays,
  selectDisplaySettings,
  selectMapExtent,
  selectSetBaseLayerResourcePlays,
  useMapStore
} from "stores/map";
import { useDebouncedCallback } from "use-debounce";

import { useDebounce } from "hooks";

import { BASE_WELL_PLAYS_QUERY_KEY, fetchBaseWellPlays } from "api/map";

import { useBaseLayerRefactor } from "components/map/hooks/useBaseLayerRefactor";
import useDataSouceAvailability from "components/map/hooks/useDataSourceAvailability";
import { useUpdateLabelTextSize } from "components/map/hooks/useUpdateLabelFontSize";
import { bboxFromExtentPolygon } from "components/map/utils/mapExtentBbox";
import {
  useUpdateFacilityIconSize,
  useUpdateLayerCircleRadius,
  useUpdateLayerLineThickness,
  useUpdateRigFracIconSize
} from "components/project/layers/hooks";

import {
  IZoomSliderProps,
  LayerSettingsRowItem,
  LayerSettingsWrapper,
  RightInputNumber,
  StyledDivider,
  ZoomSlider
} from "..";
import { LabelDensitySlider } from "../LabelDensitySlider";

interface MapLayerSettingsProps {
  dotThicknessSettings?: {
    min?: number;
    max?: number;
    value: number;
  };
  lineThicknessSettings?: {
    min?: number;
    max?: number;
    value: number;
  };
  layerName: string;
  zoomSliderProps: IZoomSliderProps;
}

// The icon-size cog shows a 1-10 stepper but stores a 1.0-2.5 icon-size
// multiplier. These two helpers are the single home for that mapping.
const displayFromSize = (size: number): number =>
  Math.round(((size - 1.0) / 1.5) * 9 + 1);

const sizeFromDisplay = (displayValue: number): number => {
  // A cleared input yields NaN from parseFloat; fall back to the base multiplier
  // so NaN never reaches the store or the map icon-size expression.
  if (Number.isNaN(displayValue)) {
    return 1.0;
  }
  const clamped = displayValue > 10 ? 10 : displayValue < 1 ? 1 : displayValue;
  return ((clamped - 1) / 9) * 1.5 + 1.0;
};

/**
 * `{minX},{minY},{maxX},{maxY}` for the base-well plays endpoint's `bbox`
 * param, derived from the map's settled viewport extent (see
 * `selectMapExtent`). Null while the map hasn't reported a viewport yet — the
 * store's initial extent is an empty polygon. The min/max walk and its
 * lon/lat clamp are shared with `RigFracDataTableWidget.tsx` — see
 * `bboxFromExtentPolygon`.
 */
const bboxParamFromExtent = (extent: GeoJSON.Polygon): Maybe<string> => {
  const bbox = bboxFromExtentPolygon(extent);
  if (!bbox) return null;
  return `${bbox.minLon},${bbox.minLat},${bbox.maxLon},${bbox.maxLat}`;
};

// The resource-play Select's "clear filter" option. Cannot be a real play
// name — `value.includes("All")` would collapse a play literally named "All"
// into clearing the filter instead of selecting it.
const ALL_PLAYS_SENTINEL = "__all__";

export const MapLayerSettings = ({
  layerName = null,
  zoomSliderProps
}: MapLayerSettingsProps): JSX.Element => {
  const baseLayerRefactor = useBaseLayerRefactor();
  const { hasGdc } = useDataSouceAvailability();
  const currentPlayList = useSelector((state: RootState) => state.filter.currentPlayList);
  const displaySettings = useMapStore(selectDisplaySettings);
  const setBaseLayerResourcePlays = useMapStore(selectSetBaseLayerResourcePlays);
  const debouncedSetBaseLayerResourcePlays = useDebouncedCallback(
    setBaseLayerResourcePlays,
    300
  );

  useEffect(() => {
    return () => {
      debouncedSetBaseLayerResourcePlays.flush();
    };
  }, [debouncedSetBaseLayerResourcePlays]);

  // The posted selection itself — see selectedPlays below.
  const baseLayerResourcePlays = useMapStore(selectBaseLayerResourcePlays);

  // Cancel a still-pending debounced pick the moment the store's selection is
  // cleared out from under this component (an org switch — see
  // useBaseLayerFilterSync.ts:159-168 — clears `baseLayerResourcePlays`
  // directly). Without this, a pick made just before the switch can still be
  // sitting in the 300ms debounce window; left alone, it fires AFTER the
  // clear and writes the OLD org's play name back onto the NEW org's filter.
  // Selecting "All (Clear Filter)" also drives this same debounced setter to
  // undefined, but by the time the store value changes here that debounce has
  // already fired — there's no pending timeout left for `cancel()` to touch.
  useEffect(() => {
    if (baseLayerResourcePlays === undefined) {
      debouncedSetBaseLayerResourcePlays.cancel();
    }
  }, [baseLayerResourcePlays, debouncedSetBaseLayerResourcePlays]);

  // Gates the plays query below so its cost — proportional to the viewport's
  // well count server-side — follows the popover that actually needs the
  // list, instead of firing on every settled pan while the layers panel is
  // merely showing the base-well node (this component is mounted
  // unconditionally there — see LayerNode.tsx).
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  // The settled map viewport (EVA-35 viewport rework) — the plays dropdown's
  // option list is now scoped to it. `mapExtent` itself only updates once the
  // camera stops moving (see MOVE_DEBOUNCE_MS in useMapLifecycle); this extra
  // debounce matches the convention RigFracDataTableWidget already uses for a
  // viewport-scoped fetch outside the core map hooks, coalescing a fresh
  // settle with the refetch it causes.
  const mapExtent = useMapStore(selectMapExtent);
  const debouncedMapExtent = useDebounce(mapExtent, 300);
  const bbox = bboxParamFromExtent(debouncedMapExtent);
  const {
    data: baseWellPlays,
    isLoading: isBaseWellPlaysLoading,
    isError: isBaseWellPlaysError
  } = useQuery({
    queryKey: [BASE_WELL_PLAYS_QUERY_KEY, bbox],
    queryFn: () => fetchBaseWellPlays(bbox),
    enabled:
      layerName === BASE_WELL_LAYER &&
      baseLayerRefactor === true &&
      isSettingsOpen &&
      !!bbox,
    // Keeps the previous viewport's options on screen while a pan's new bbox
    // is in flight, instead of flashing to the loading/empty state on every
    // settle. Safe now that selection is client-owned (see selectedPlays
    // below) — this query only ever feeds the option list, never what's
    // selected, so stale options can't clobber a pick.
    keepPreviousData: true,
    // Matches eva-map-svc's own viewport-plays cache TTL (30s) — anything
    // longer serves a stale list for a dropdown reopened on the same bbox
    // without panning, which is exactly the case that server-side cache
    // exists for.
    staleTime: 30_000
  });
  const { updateLayerCircleRadius } = useUpdateLayerCircleRadius();
  const { updateLayerLineThickness } = useUpdateLayerLineThickness();
  const { updateFacilityIconSize } = useUpdateFacilityIconSize();
  const { updateRigIconSize, updateFracIconSize } = useUpdateRigFracIconSize();
  const {
    updateWellLabelTextSize,
    updateFacilityLabelTextSize,
    updateShapefileDetailLabelTextSize,
    updateShapefileNameLabelTextSize,
    updateSectionLabelTextSize,
    updateGridLabelTextSize
  } = useUpdateLabelTextSize();

  const [lineThickness, setLineThickness] = useState(displaySettings.wellLineThickness);
  const [dotThickness, setDotThickness] = useState(displaySettings.wellDotThickness);
  const [facilityIconSize, setFacilityIconSize] = useState(
    displaySettings.facilityIconSize
  );
  // Rig/frac icon sizes are read straight from the store (the source of truth),
  // NOT mirrored into local state. A project View resets/restores these on
  // switch (see applyViewDisplaySettings); a local mirror initialised once at
  // mount would show the previous view's size after a switch. Reading the store
  // directly keeps the stepper in sync with whatever the active View applied.

  // The option list, scoped to the current map viewport, already sorted A→Z
  // case-insensitively by the server — see fetchBaseWellPlays. On its own
  // this stays A→Z; it only needs re-sorting once merged with a panned-out
  // selection below.
  const playNamesInViewport = baseWellPlays ?? [];

  // Selection is entirely client-owned (EVA-35 viewport rework): the server
  // no longer reports which plays are selected (`inFilter` is gone from the
  // payload), so `selectedPlays` simply mirrors `baseLayerResourcePlays` —
  // what the user picked and what got posted — rather than anything read
  // back from the plays query. Local state exists only so the multi-select
  // stays responsive to a click before its own 300ms debounce round-trips
  // into the store; the effect below re-mirrors it for changes that don't
  // come from this component's own onChange (a View restore, an org switch
  // clearing the selection).
  const [selectedPlays, setSelectedPlays] = useState<string[]>(
    baseLayerResourcePlays ? [...baseLayerResourcePlays] : []
  );
  useEffect(() => {
    setSelectedPlays(baseLayerResourcePlays ? [...baseLayerResourcePlays] : []);
  }, [baseLayerResourcePlays]);

  // A play that pans out of view stays selected (EVA-35): the option list is
  // now viewport-scoped, so a prior selection can reference a play the
  // current response doesn't include. Antd drops a `value` entry with no
  // matching Option, so add one for exactly that case.
  const selectedPlaysOutsideViewport = selectedPlays.filter(
    (play) => !playNamesInViewport.includes(play)
  );

  // Merging two already-sorted lists (the viewport response and a panned-out
  // selection) doesn't produce one sorted list — a panned-out selection would
  // otherwise render ahead of the whole viewport list regardless of where its
  // name falls A→Z. This is the one unavoidable client-side sort: EVA-35
  // decision 4 is "names only, sorted A to Z" for the list the user sees.
  const playOptions =
    baseLayerRefactor === true
      ? [...selectedPlaysOutsideViewport, ...playNamesInViewport].sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: "base" })
        )
      : Array.from(new Set([...currentPlayList, ...selectedPlays])).sort();

  let defaultLabelSize = 0;
  switch (layerName) {
    case FACILITY_LABEL_LAYER:
      defaultLabelSize = displaySettings.facilityLabelSize;
      break;
    case WELL_LABEL_LAYER:
      defaultLabelSize = displaySettings.wellLabelSize;
      break;
    case SHAPEFILE_DETAILS_LABEL_LAYER:
      defaultLabelSize = displaySettings.shapefileDetailsLabelSize;
      break;
    case SHAPEFILE_NAME_LABELS_LAYER:
      defaultLabelSize = displaySettings.shapefileNameLabelSize;
      break;
    case SECTION_LABEL_LAYER:
      defaultLabelSize = displaySettings.sectionLabelSize;
      break;
    default:
      break;
  }
  const [labelSize, setLabelSize] = useState(defaultLabelSize);

  // Each US/Texas grid label layer carries its own size, keyed by layer name.
  // The value is read straight from the store (like the rig/frac icon sizes)
  // so a View restore shows in the stepper, and it falls back to the size the
  // layer was created with until the user picks one.
  const gridLabelDefaultSize = GRID_LABEL_DEFAULT_SIZES[layerName];
  const isGridLabelLayer = gridLabelDefaultSize !== undefined;
  const gridLabelSize =
    displaySettings.gridLabelSizes?.[layerName] ?? gridLabelDefaultSize;

  const showLabelSizeSettings =
    layerName === WELL_LABEL_LAYER ||
    layerName === FACILITY_LABEL_LAYER ||
    layerName === SHAPEFILE_DETAILS_LABEL_LAYER ||
    layerName === SHAPEFILE_NAME_LABELS_LAYER ||
    layerName === SECTION_LABEL_LAYER ||
    isGridLabelLayer;

  // The facility, rig, and frac layers all render the same icon-size stepper;
  // only the layer, its store mirror, and its updater differ.
  const iconSizeConfigs = [
    {
      layerName: FACILITY_LAYER,
      value: facilityIconSize,
      setter: setFacilityIconSize,
      updater: updateFacilityIconSize,
      testId: "facilityIconSize"
    },
    {
      layerName: RIG_LAYER,
      // Store-driven (no local mirror) so a View switch's reset/restore shows.
      value: displaySettings.rigIconSize,
      updater: updateRigIconSize,
      testId: "rigIconSize"
    },
    {
      layerName: FRAC_LAYER,
      value: displaySettings.fracIconSize,
      updater: updateFracIconSize,
      testId: "fracIconSize"
    }
  ];

  const layerSettings = (
    <LayerSettingsWrapper direction="vertical" split={<StyledDivider />}>
      <LayerSettingsRowItem isFirst isSlider>
        <ZoomSlider
          layerName={zoomSliderProps.layerName}
          isShapefileLayer={zoomSliderProps.isShapefileLayer}
        />
      </LayerSettingsRowItem>
      {layerName === WELL_LAYER && (
        <LayerSettingsRowItem>
          <label>Line Thickness</label>
          <RightInputNumber
            data-testid="layerThickness"
            type="number"
            min={1}
            max={10}
            value={lineThickness}
            onChange={(val) => {
              const nextThickness =
                parseFloat(val.target.value) > 10 ? 10 : parseFloat(val.target.value);

              setLineThickness(nextThickness);
              updateLayerLineThickness(nextThickness);
            }}
          />
        </LayerSettingsRowItem>
      )}

      {layerName === WELL_LAYER && (
        <LayerSettingsRowItem isLast>
          <label>Dot Thickness</label>
          <RightInputNumber
            data-testid="dotThickness"
            type="number"
            min={1}
            max={10}
            value={dotThickness}
            onChange={(val) => {
              const nextThickness =
                parseFloat(val.target.value) > 10 ? 10 : parseFloat(val.target.value);

              setDotThickness(nextThickness);
              updateLayerCircleRadius(nextThickness);
            }}
          />
        </LayerSettingsRowItem>
      )}

      {iconSizeConfigs
        .filter((config) => config.layerName === layerName)
        .map((config) => (
          <LayerSettingsRowItem isLast key={config.testId}>
            <label>Icon Size</label>
            <RightInputNumber
              data-testid={config.testId}
              type="number"
              min={1}
              max={10}
              step={1}
              value={displayFromSize(config.value)}
              onChange={(val) => {
                const nextSize = sizeFromDisplay(parseFloat(val.target.value));
                // Facility keeps a local mirror; rig/frac are store-driven and
                // omit the setter (the store write via updater re-renders them).
                config.setter?.(nextSize);
                config.updater(nextSize);
              }}
            />
          </LayerSettingsRowItem>
        ))}

      {(layerName === WELL_LABEL_LAYER ||
        layerName === FACILITY_LABEL_LAYER ||
        layerName === SHAPEFILE_DETAILS_LABEL_LAYER ||
        layerName === SHAPEFILE_NAME_LABELS_LAYER) && (
        <LayerSettingsRowItem isSlider>
          <LabelDensitySlider layerName={layerName} />
        </LayerSettingsRowItem>
      )}

      {showLabelSizeSettings && (
        <LayerSettingsRowItem isLast>
          <label>Label Size</label>
          <RightInputNumber
            data-testid="labelSize"
            type="number"
            min={6}
            max={48}
            value={isGridLabelLayer ? gridLabelSize : labelSize}
            onChange={(val) => {
              const nextSize =
                parseFloat(val.target.value) > 48 ? 48 : parseFloat(val.target.value);
              if (isGridLabelLayer) {
                // A cleared input parses to NaN. Skip the write so the stored
                // size keeps its last valid value instead of sending NaN to
                // the map, and leave the box empty for the user to retype.
                if (!Number.isNaN(nextSize)) {
                  updateGridLabelTextSize(layerName, nextSize);
                }
                return;
              }
              setLabelSize(nextSize);
              switch (layerName) {
                case FACILITY_LABEL_LAYER:
                  updateFacilityLabelTextSize(nextSize);
                  break;
                case WELL_LABEL_LAYER:
                  updateWellLabelTextSize(nextSize);
                  break;
                case SHAPEFILE_DETAILS_LABEL_LAYER:
                  updateShapefileDetailLabelTextSize(nextSize);
                  break;
                case SHAPEFILE_NAME_LABELS_LAYER:
                  updateShapefileNameLabelTextSize(nextSize);
                  break;
                case SECTION_LABEL_LAYER:
                  updateSectionLabelTextSize(nextSize);
                  break;
                default:
                  break;
              }
            }}
          />
        </LayerSettingsRowItem>
      )}

      {layerName === BASE_WELL_LAYER && (baseLayerRefactor === true || hasGdc) && (
        <LayerSettingsRowItem isLast>
          <div
            style={{
              display: "grid",
              gap: "8px",
              gridTemplateColumns: "1fr auto",
              alignItems: "center",
              width: "100%"
            }}>
            <label>Resource Plays</label>
            <Select
              data-testid="resourcePlays"
              mode="multiple"
              placeholder="Filter by Resource Play"
              value={selectedPlays}
              loading={isBaseWellPlaysLoading}
              notFoundContent={
                isBaseWellPlaysError
                  ? "Couldn't load resource plays"
                  : "No resource plays found"
              }
              onChange={(value: string[]) => {
                // The ALL_PLAYS_SENTINEL means "no filter" — zero predicates is
                // a valid state (every well the org can see). Selecting every
                // play CURRENTLY IN VIEW no longer means the same thing now
                // that the option list is viewport-scoped (EVA-35): there can
                // be plays elsewhere the user hasn't panned to, so covering
                // every visible option must stay an explicit list rather than
                // collapsing to "everything".
                const isAllSentinel = value.includes(ALL_PLAYS_SENTINEL);
                setSelectedPlays(isAllSentinel ? [] : value);
                debouncedSetBaseLayerResourcePlays(isAllSentinel ? undefined : value);
              }}
              style={{ width: "200px" }}
              maxTagCount="responsive"
              allowClear>
              <Select.Option key={ALL_PLAYS_SENTINEL} value={ALL_PLAYS_SENTINEL}>
                All (Clear Filter)
              </Select.Option>
              {playOptions.map((play) => (
                <Select.Option key={play} value={play}>
                  {play}
                </Select.Option>
              ))}
            </Select>
          </div>
        </LayerSettingsRowItem>
      )}
    </LayerSettingsWrapper>
  );

  return (
    <Tooltip title="Settings">
      <Popover
        content={layerSettings}
        placement="bottomLeft"
        trigger="click"
        arrowPointAtCenter={true}
        // No open animation: react-range positions its thumbs from
        // getBoundingClientRect, so any re-render during the zoom-big scale
        // (e.g. a map zoom tick on first load) bakes in a shrunken track and
        // both thumbs land at the start of the slider.
        transitionName=""
        open={isSettingsOpen}
        onOpenChange={setIsSettingsOpen}>
        <Button type="text" aria-label="Edit">
          <Icon path={mdiCog} size={"18px"} />
        </Button>
      </Popover>
    </Tooltip>
  );
};
